import React from "react";
import { Box, Card, CardContent, Typography, Button, Divider, useTheme } from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import Labels from "../../../labels/Labels";

export default function EmployerSidebar({
  employerData,
  subDepartments,
  isLoading,
  isError,
  onShowStats,
  onShowMovements,
  selected,
}) {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card sx={{ position: "sticky", top: 20, height: "fit-content" }}>
        <CardContent>
          <Typography>Loading...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (isError || !employerData) {
    return (
      <Card sx={{ position: "sticky", top: 20, height: "fit-content" }}>
        <CardContent>
          <Typography color="error">Error loading employer data</Typography>
        </CardContent>
      </Card>
    );
  }

  const {
    employerDescription,
    currentBalance,
    creditsThisMonth,
    debitsThisMonth,
    creditsThisWeek,
    debitsThisWeek,
  } = employerData;

  // Calculate summary stats
  const totalSubDepartments = subDepartments.length;
  const activeSubDepartments = subDepartments.filter(
    (dept) => dept.currentBalance > 0
  ).length;
  const activeThisWeek = subDepartments.filter(
    (dept) =>
      dept.creditsThisWeek > 0 ||
      dept.debitsThisWeek > 0
  ).length;
  const totalSubBalance = subDepartments.reduce(
    (sum, dept) => sum + (dept.currentBalance || 0),
    0
  );

  const netChangeThisMonth = creditsThisMonth - debitsThisMonth;

  return (
    <Card
      sx={{
        position: "sticky",
        top: 20,
        height: "fit-content",
        border: selected ? "2px solid " + theme.palette.primary.main : "1px solid #ddd",
      }}
    >
      <CardContent>
        {/* Employer Header */}
        <Box
          sx={{
            background: theme.palette.primary.main,
            borderRadius: 2,
            padding: 3,
            color: "white",
            textAlign: "center",
            marginBottom: 2,
          }}
        >
          <Typography variant="h4" >
            {employerDescription}
          </Typography>
          <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
            {Labels.getLabel("queryBalance.EmployerBalance", "Employer Balance")}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ marginTop: 1 }}>
            $ {Number(currentBalance).toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, marginTop: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssessmentIcon />}
              onClick={() => onShowStats(employerData.idEmployer)}
              sx={{
                flex: 1,
                backgroundColor: "white",
                "&:hover": { backgroundColor: "#f0fdfa" },
              }}
            >
              {Labels.getLabel("queryBalance.Stats", "Stats")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ListAltIcon />}
              onClick={() => onShowMovements(employerData.idEmployer)}
              sx={{
                flex: 1,
                backgroundColor: "white",
                "&:hover": { backgroundColor: "#f0fdfa" },
              }}
            >
              {Labels.getLabel("queryBalance.Movements", "Movements")}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        {/* This Month Activity */}
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {Labels.getLabel("queryBalance.ThisMonthActivity", "This Month Activity")}
        </Typography>
        <Box sx={{ marginBottom: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("queryBalance.Credits", "Credits")}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="#0d9488">
              $ {Number(creditsThisMonth).toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("queryBalance.Debits", "Debits")}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="#d32f2f">
              $ {Number(debitsThisMonth).toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("queryBalance.NetChange", "Net Change")}
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              color={netChangeThisMonth >= 0 ? "#0d9488" : "#d32f2f"}
            >
              {netChangeThisMonth >= 0 ? "+" : ""}$
              {Number(netChangeThisMonth).toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        {/* This Week Activity */}
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {Labels.getLabel("queryBalance.ThisWeekActivity", "This Week Activity")}
        </Typography>
        <Box sx={{ marginBottom: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("queryBalance.Credits", "Credits")}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              $ {Number(creditsThisWeek).toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("queryBalance.Debits", "Debits")}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              $ {Number(debitsThisWeek).toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        {/* Sub-Balance Summary */}
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {Labels.getLabel("queryBalance.SubBalanceSummary", "Sub-Balance Summary")}
        </Typography>
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("queryBalance.TotalSubBalances", "Total Sub-Balances")}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {totalSubDepartments}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("queryBalance.Active", "Active")} (Balance &gt; $0)
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="#14b8a6">
              {activeSubDepartments}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("queryBalance.ActiveThisWeek", "Active This Week")}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="#14b8a6">
              {activeThisWeek}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("queryBalance.TotalSubBalance", "Total Sub-Balance")}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              $ {Number(totalSubBalance).toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
